import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/supabase';
import type { PlannedCategory } from './PlannedCategory';
import { fromDecimal, toDecimal } from '@/lib/utils';

export type MonthlyPlan = Database['public']['Tables']['monthly_plan']['Row'] & {
  planned_categories?: PlannedCategory[];
};
type MonthlyPlanInsert = Database['public']['Tables']['monthly_plan']['Insert'];
type MonthlyPlanUpdate = Database['public']['Tables']['monthly_plan']['Update'];

export class MonthlyPlanService {
  static async filter(query: Partial<MonthlyPlan>): Promise<MonthlyPlan[]> {
    let queryBuilder = supabase.from('monthly_plan').select(`
      *,
      planned_categories(*)
    `);

    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined) {
        queryBuilder = queryBuilder.eq(key, value);
      }
    });

    // Garantir que retornemos sempre o plano mais recente quando houver múltiplos registros
    queryBuilder = queryBuilder.order('created_at', { ascending: false });

    const { data, error } = await queryBuilder;
    if (error) throw error;
    return data || [];
  }

  static async create(data: Omit<MonthlyPlanInsert, 'id' | 'created_at' | 'updated_at'> & {
    total_planned_income?: number;
    total_planned_expenses?: number;
    total_received_income?: number;
    total_spent_expenses?: number;
  }): Promise<MonthlyPlan> {
    const { data: newPlan, error } = await (supabase as any)
      .from('monthly_plan')
      .insert([{
        ...data,
        total_planned_income: data.total_planned_income ? toDecimal(data.total_planned_income) : '0.00',
        total_planned_expenses: data.total_planned_expenses ? toDecimal(data.total_planned_expenses) : '0.00',
        total_received_income: data.total_received_income ? toDecimal(data.total_received_income) : '0.00',
        total_spent_expenses: data.total_spent_expenses ? toDecimal(data.total_spent_expenses) : '0.00',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }])
      .select(`
        *,
        planned_categories(*)
      `)
      .single();

    if (error) throw error;
    if (!newPlan) throw new Error('Failed to create plan');

    return newPlan;
  }

  static async update(id: string, data: Omit<MonthlyPlanUpdate, 'id' | 'created_at' | 'updated_at'> & {
    total_planned_income?: number;
    total_planned_expenses?: number;
    total_received_income?: number;
    total_spent_expenses?: number;
  }): Promise<MonthlyPlan> {
    const updateData: any = {
      ...data,
      updated_at: new Date().toISOString()
    };

    if (typeof data.total_planned_income === 'number') {
      updateData.total_planned_income = toDecimal(data.total_planned_income);
    }
    if (typeof data.total_planned_expenses === 'number') {
      updateData.total_planned_expenses = toDecimal(data.total_planned_expenses);
    }
    if (typeof data.total_received_income === 'number') {
      updateData.total_received_income = toDecimal(data.total_received_income);
    }
    if (typeof data.total_spent_expenses === 'number') {
      updateData.total_spent_expenses = toDecimal(data.total_spent_expenses);
    }

    const { data: updatedPlan, error } = await (supabase as any)
      .from('monthly_plan')
      .update(updateData)
      .eq('id', id)
      .select(`
        *,
        planned_categories(*)
      `)
      .single();

    if (error) throw error;
    if (!updatedPlan) throw new Error('Plan not found');

    return updatedPlan;
  }

  static async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('monthly_plan')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
}

// Re-export para manter compatibilidade com código existente
export const MonthlyPlan = MonthlyPlanService;
